import Slider from '@mui/material/Slider'
import classNames from 'classnames'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { UserTypes } from 'store/auth/types'
import { roundToTwoDecimals } from 'utils/number'
import styles from './EditInstalment.module.scss'
import { InstalmentForm } from './types'
import { debounce } from 'es-toolkit/function'
import useWindowSize from 'hooks/useWindowSize'
import { isActiveContract, isTerminatedContractPastEndDate } from 'utils/contracts'
import useGetAccessRight from 'hooks/useSelectAccessRight.ts'
import { addNewInstalmentToContract } from './utils'
import { newRequest } from 'utils/request'
import { routes } from 'types/routes'
import { log } from 'utils/logging.ts'
import { isResponseCode } from 'utils/errors.ts'
import { Navigate, useNavigate } from 'react-router'
import { Response } from 'types/request'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { useGetLanguage } from 'hooks/use-get-language'
import { Button, Card, Heading, TextField } from '@boltenergy-be/design-system'
import EmptyState from 'components/return-later/EmptyState.tsx'
import { updateInstalment } from 'store/contact/thunks'
import { useGetBillShockQuery } from 'store/queries/bolt-backend/billing-contracts'
import EditLayout from 'layouts/edit-layout/EditLayout.tsx'
import LoadingSkeleton from 'components/loading-skeleton/LoadingSkeleton.tsx'
import { formatCurrency } from 'utils/format.ts'
import { selectCurrentBillingContract } from 'store/contact/slice.ts'
import { trackEvent } from 'utils/tracking.ts'
import {
  ENERGY_TYPES,
  GetBillShockDataResponseBody,
  LANGUAGES,
  ServiceContractElectricity,
  ServiceContractGas
} from '@boltenergy-be/shared'
import { calculateInstalment } from 'utils/instalment.ts'
import InstalmentBreakdownDrawer from 'pages/app/billing/edit-instalment/components/instalment-breakdown-drawer/InstalmentBreakdownDrawer.tsx'
import EstimatedSettlement from 'components/estimated-settlement/EstimatedSettlement.tsx'
import Link from 'components/link/Link.tsx'
import useGetCurrentContractMeterReadings from 'hooks/useGetCurrentContractMeterReadings.ts'
import dayjs from 'dayjs'
import { useBeforeunload } from 'react-beforeunload'
import InstalmentCard from 'components/instalment-card/InstalmentCard.tsx'
import CustomSliderMarker from './components/custom-slider-marker/CustomSliderMarker.tsx'
import CustomSliderMarkerLabel from './components/custom-slider-marker/CustomSliderMarkerLabel.tsx'
import { MAXIMUM_INSTALMENT_INPUT } from 'pages/app/billing/edit-instalment/constants.ts'
import { IS_PROD_TESTING } from 'constants/envs.ts'
import { ACCESS_RIGHT_KEYS } from 'store/access-rights/constants.ts'
import { selectUrlSearchParams } from 'store/app/slice.ts'
import { UrlSearchParamsKeys } from 'store/app/types.ts'
import { hasEnergyTypeValue } from 'store/access-rights/utils.ts'
import { generateBillShockAxiosRequestConfig } from 'store/queries/bolt-backend/billing-contracts/utils.ts'

const EditInstalment = () => {
  // REDUX STORE
  const { userType } = useStoreSelector((store) => store.auth)
  const { selected, contact, loading } = useStoreSelector((store) => store.contact)
  const billingContract = useStoreSelector(selectCurrentBillingContract)
  const proposedInstalmentFromUrl = useStoreSelector(selectUrlSearchParams)[UrlSearchParamsKeys.PROPOSED_INSTALMENT]
  const dispatch = useStoreDispatch()

  // Contracts
  const { electricity, gas } = billingContract.serviceContracts
  const serviceContracts = { electricity, gas: isActiveContract(gas) ? gas : undefined }

  // i18n
  const { t } = useTranslation(['billing', 'validation', 'common'])
  const language = useGetLanguage()

  // Access rights
  const billingCyclesAccessRights = useGetAccessRight(ACCESS_RIGHT_KEYS.billingCycles)
  const meterReadingsAccessRights = useGetAccessRight(ACCESS_RIGHT_KEYS.meterReadings)
  const instalmentAccessRights = useGetAccessRight(ACCESS_RIGHT_KEYS.instalment)
  const terminatedContractPastEndDate = isTerminatedContractPastEndDate(electricity)

  // Redux queries
  const { data: billShockData, isLoading: billShockLoading } = useGetBillShockQuery(
    {
      billingContractId: selected.billingContract,
      email: contact?.email || '',
      serviceContracts,
      proposedInstalmentFromUrl
    },
    {
      skip: !contact || !selected.billingContract || !billingCyclesAccessRights.showContent.current || terminatedContractPastEndDate
    }
  )
  const { data: meterReadingsData, isLoading: meterReadingsLoading } = useGetCurrentContractMeterReadings({
    customSkip: !hasEnergyTypeValue(meterReadingsAccessRights.canAccess.edit)
  })

  // Constants
  const billShock = billShockData?.billShock
  const electricityContractStarted = isActiveContract(electricity)
  const currentElectricityInstalment = electricityContractStarted ? Number(serviceContracts.electricity.detail.instalment || 0) : 0
  const currentGasInstalment = serviceContracts.gas ? Number(serviceContracts.gas?.detail.instalment || 0) : 0
  const currentInstalment = roundToTwoDecimals(currentElectricityInstalment + currentGasInstalment)
  const hasEffectiveGasContract = typeof gas !== 'undefined' && isActiveContract(gas)
  const prefixOrSuffix = language === LANGUAGES.NL ? { prefix: { as: '€' } } : { suffix: { as: '€' } }

  // Local state
  const [billShockAmount, setBillShockAmount] = useState<number | null>(null)
  const [electricityRatio, setElectricityRatio] = useState<number>(0)
  const [gasRatio, setGasRatio] = useState<number>(0)
  const [hoveringOnMark, setHoveringOnMark] = useState<boolean>(false)
  const [instalmentSaved, setInstalmentSaved] = useState<boolean>(false)
  const [maxSliderBoundary, setMaxSliderBoundary] = useState<number>(0)
  const [minInstalment, setMinInstalment] = useState<number>(0)
  const [proposedInstalment, setProposedInstalment] = useState<number | undefined>(undefined)
  const [refetchingBillShock, setRefetchingBillShock] = useState<boolean>(false)
  const [showBreakdownModal, setShowBreakdownModal] = useState<boolean>(false)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [sliderMarks, setSliderMarks] = useState<Array<{ value: number; label: string | ReactNode; id: 'current' | 'proposed' }>>([])
  const [proposedFromUrlUsed, setProposedFromUrlUsed] = useState<boolean>(false)

  // Window size
  const { isMobile } = useWindowSize()

  // Router
  const navigate = useNavigate()

  // React hook form
  const {
    formState: { errors, isValid },
    handleSubmit,
    register,
    setValue,
    watch
  } = useForm<InstalmentForm>({
    mode: 'onChange',
    defaultValues: {
      totalInstalment: Number(currentInstalment),
      electricityInstalment: Number(currentElectricityInstalment),
      gasInstalment: Number(currentGasInstalment)
    },
    shouldFocusError: false
  })

  // React Hook Form Watch values
  const watchElectricityInstalment = watch('electricityInstalment')
  const watchGasInstalment = watch('gasInstalment')
  const watchTotalInstalment = watch('totalInstalment')

  /**
   * Calculate instalment data when bill shock stopped loading
   */
  useEffect(() => {
    if (!billShockLoading) calculateInstalmentData()
  }, [billShockLoading])

  // Shows dialog when user tries to reload the page without confirming
  useBeforeunload((e: any) => {
    if (currentInstalment !== watchTotalInstalment && !instalmentSaved) {
      e.preventDefault()
    }
  })

  /**
   * Checks if there are changes in the instalment values compared to the current ones (used for the "unsaved changes" dialog)
   * @returns {boolean}
   */
  const checkHasInstalmentChanges = (): boolean => {
    const differentTotal = watchTotalInstalment !== currentInstalment

    // Compare electricity and gas instalments separately if there's an effective gas contract
    if (hasEffectiveGasContract) {
      const differentElectricity = watchElectricityInstalment !== currentElectricityInstalment
      const differentGas = watchGasInstalment !== currentGasInstalment

      return differentTotal || differentElectricity || differentGas
    }

    // If there's no effective gas contract, we can just compare the total instalment
    return differentTotal
  }

  /**
   * Initializes the instalment page data fetch & sets the correct values in the store
   */
  const calculateInstalmentData = () => {
    const instalmentData = calculateInstalment({
      billShock,
      userType,
      initialMinInstalment: minInstalment,
      initialMaxSliderBoundary: maxSliderBoundary,
      hasEffectiveGasContract,
      currentInstalment: {
        total: currentInstalment,
        electricity: currentElectricityInstalment,
        gas: currentGasInstalment
      },
      proposedInstalmentFromUrl
    })

    setMaxSliderBoundary(instalmentData.max)
    setMinInstalment(instalmentData.min)
    setBillShockAmount(instalmentData.billShock || null)
    setProposedInstalment(instalmentData.proposed)
    setElectricityRatio(instalmentData.ratio.electricity)
    setGasRatio(instalmentData.ratio.gas)

    // Set the instalmentData to the proposed from the url when available and not changed yet
    if (instalmentData.proposedInstalmentFromUrl && !proposedFromUrlUsed) {
      handleInstalmentChange(
        instalmentData.proposedInstalmentFromUrl,
        instalmentData.proposedInstalmentFromUrl * instalmentData.ratio.electricity,
        instalmentData.proposedInstalmentFromUrl * instalmentData.ratio.gas,
        true
      )
      setProposedFromUrlUsed(true)
    }

    const newSliderMarks: typeof sliderMarks = []
    const proposed = instalmentData.proposed

    newSliderMarks.push(
      proposed
        ? {
            id: 'proposed',
            value: proposed,
            label: (
              <>
                <strong>{formatCurrency(proposed, { language })}</strong>
                <small>{t(($) => $.editInstalment.slider.proposed)}</small>
              </>
            )
          }
        : {
            id: 'current',
            value: currentInstalment,
            label: (
              <>
                <strong>{formatCurrency(currentInstalment, { language })}</strong>
                <small>{t(($) => $.editInstalment.slider.current)}</small>
              </>
            )
          }
    )

    setSliderMarks(newSliderMarks)
  }

  /**
   * Handles the submit after validation by RHF
   * TODO: refactor to redux query mutation with invalidation on billshock query
   *
   * @param data
   */
  const onSubmit = async (data: InstalmentForm) => {
    setSubmitting(true)

    const { electricityInstalment, gasInstalment } = data

    if (IS_PROD_TESTING) {
      alert(`Instalment updated to ${electricityInstalment + (gasInstalment || 0)} (not really, since this is prod testing)`)
      void navigate(-1)
      return
    }

    const updatedElectricityContract = addNewInstalmentToContract(electricity, electricityInstalment) as ServiceContractElectricity

    let updatedGasContract
    if (hasEffectiveGasContract && gasInstalment) {
      updatedGasContract = addNewInstalmentToContract(gas, gasInstalment) as ServiceContractGas
    }

    setInstalmentSaved(true)

    // Save the contracts
    await dispatch(
      updateInstalment({
        billingContractId: billingContract.contractNumber,
        customerNumber: billingContract.customerNumber,
        electricity: updatedElectricityContract,
        gas: updatedGasContract
      })
    )

    trackEvent(
      { category: 'billing', object: 'edit_instalment', action: 'finish' },
      {
        newEstimatedSettlement: billShockAmount,
        initialBillShockAmount: billShock?.settlement?.billShockAmount,
        newInstalmentTrend: data.totalInstalment < currentInstalment ? 'less' : data.totalInstalment > currentInstalment ? 'more' : 'same',
        newInstalmentComparedToProposed: !proposedInstalment
          ? 'no_proposed'
          : data.totalInstalment < proposedInstalment
            ? 'less'
            : data.totalInstalment > proposedInstalment
              ? 'more'
              : 'same',
        isProposedInstalment: data.totalInstalment === proposedInstalment
      }
    )

    setSubmitting(false)

    void navigate(-1)
  }

  /**
   * Re-fetches bill shock data
   * TODO: check if this can be combined with the redux query for billshock
   */
  const refetchBillShock = async (updatedElectricityContract: ServiceContractElectricity, updatedGasContract?: ServiceContractGas) => {
    setRefetchingBillShock(true)

    const options = generateBillShockAxiosRequestConfig(
      billingContract.contractNumber,
      contact?.email || '',
      updatedElectricityContract,
      updatedGasContract
    )
    const { success, data, message, errorCode }: Response<GetBillShockDataResponseBody> = await newRequest(options)

    if (success && data) {
      if (data.billShock) {
        setBillShockAmount(data.billShock.settlement.billShockAmount)

        if (data.billShock.instalment.proposed > currentInstalment) {
          setProposedInstalment(data.billShock.instalment.proposed)
        }
      }
    } else {
      const responseCode = [errorCode, message].find(isResponseCode)
      log({
        error: message || 'Error while fetching bill shock data or empty response',
        identifier: `[billing:refetchBillShock${responseCode ? `:${responseCode}` : ''}]`,
        errorCode: responseCode
      })
    }

    setRefetchingBillShock(false)
  }

  const refetchBillShockDebounce = useRef(debounce(refetchBillShock, 500))

  /**
   * Handles the instalment change event (both total & fuel fields)
   *
   * @param {number} newTotalInstalment
   * @param {number} [electricityInputValue]
   * @param {number} [gasInputValue]
   * @param {boolean} [overwrite] - whether to overwrite the current values with the given ones or calculate them based on the ratios (used when clicking on the marks, to avoid "jumping" values)
   */
  const handleInstalmentChange = (
    newTotalInstalment: number,
    electricityInputValue?: number,
    gasInputValue?: number,
    overwrite?: boolean
  ) => {
    setInstalmentSaved(false)

    const totalInstalment = newTotalInstalment || 0

    let electricityInstalment = electricityInputValue || watchElectricityInstalment
    let gasInstalment = gasInputValue || watchGasInstalment

    // The field that was changed was the "totalInstalment" field
    if (newTotalInstalment && !overwrite) {
      electricityInstalment = electricityRatio === 0 ? 10 : roundToTwoDecimals(totalInstalment * electricityRatio)
      gasInstalment = electricityRatio === 0 ? totalInstalment - 10 : roundToTwoDecimals(totalInstalment * gasRatio)
    }

    setValue('totalInstalment', totalInstalment, { shouldValidate: true })
    setValue('electricityInstalment', electricityInstalment, { shouldValidate: true })
    setValue('gasInstalment', gasInstalment, { shouldValidate: true })

    // Refetch billShock (if it was available during pageload)
    if (billShockAmount !== null) {
      const updatedElectricityContract = addNewInstalmentToContract(electricity, electricityInstalment) as ServiceContractElectricity

      let updatedGasContract
      if (hasEffectiveGasContract && gasInstalment) {
        updatedGasContract = addNewInstalmentToContract(gas, gasInstalment) as ServiceContractGas
      }

      refetchBillShockDebounce.current(updatedElectricityContract, updatedGasContract)
    }
  }

  /**
   * Function to combine meter readings checks
   * @returns {boolean}
   */
  const checkCanEditMeterReadings = () => {
    const canEditElectricityMeterReadings =
      meterReadingsAccessRights.canAccess.edit[ENERGY_TYPES.electricity] &&
      !!meterReadingsData?.electricity &&
      dayjs(meterReadingsData?.electricity?.date).isBefore(dayjs().subtract(1, 'month'))
    const canEditGasMeterReadings =
      meterReadingsAccessRights.canAccess.edit[ENERGY_TYPES.gas] &&
      !!meterReadingsData?.gas &&
      dayjs(meterReadingsData?.gas?.date).isBefore(dayjs().subtract(1, 'month'))

    return canEditElectricityMeterReadings || canEditGasMeterReadings
  }

  return !instalmentAccessRights.canAccess ? (
    <Navigate to={routes.BILLING_INVOICES} replace />
  ) : (
    <EditLayout title={t(($) => $.editInstalment.title)} classes={{ body: styles.body }} backUrl={routes.BILLING_INSTALMENT_CHECK}>
      {!instalmentAccessRights.showContent ? (
        <EmptyState description={t(($) => $.editInstalment.returnLater)} />
      ) : (
        <>
          {billingCyclesAccessRights.showContent.current && (billShockLoading || meterReadingsLoading) ? (
            <LoadingSkeleton>
              <LoadingSkeleton.Rectangle height={10} className="mt-300" />

              <LoadingSkeleton.Rectangle aspectRatio="2 / 1" className="mt-600" />
            </LoadingSkeleton>
          ) : (
            <>
              {billingCyclesAccessRights.canAccess.current && checkCanEditMeterReadings() && (
                <Card color="neutral" as="section" className={styles['meter-readings-card']}>
                  <Card.Title variant="h5" serif={false}>
                    {t(($) => $.editInstalment.addMeterReadings.title)}
                  </Card.Title>
                  <p>{t(($) => $.editInstalment.addMeterReadings.description)}</p>
                  <Link representation="button" isFullwidth href={routes.CONSUMPTION_EDIT_METER_READINGS}>
                    {t(($) => $.editInstalment.addMeterReadings.action)}
                  </Link>
                </Card>
              )}

              <form id="total-instalment" className={styles['instalment-form']} onSubmit={handleSubmit(onSubmit)}>
                <TextField
                  {...register('totalInstalment', {
                    required: t(($) => $.required, {
                      ns: 'validation'
                    }),
                    min: {
                      value: minInstalment,
                      message: t(($) => $.instalment.min, {
                        amount: formatCurrency(minInstalment, { language }),
                        ns: 'validation'
                      })
                    },
                    max: {
                      value: MAXIMUM_INSTALMENT_INPUT,
                      message: t(($) => $.instalment.max, {
                        amount: formatCurrency(MAXIMUM_INSTALMENT_INPUT, { language }),
                        ns: 'validation'
                      })
                    },
                    validate: {
                      ...(userType === UserTypes.CUSTOMER && {
                        minElectricityInstalment: () =>
                          watchElectricityInstalment !== 0 ||
                          t(($) => $.instalment.minElectricityInstalment, {
                            ns: 'validation'
                          })
                      }),
                      ...(hasEffectiveGasContract &&
                        userType === UserTypes.CUSTOMER &&
                        typeof watchGasInstalment !== 'undefined' && {
                          minGasInstalment: () =>
                            watchGasInstalment !== 0 ||
                            t(($) => $.instalment.minGasInstalment, {
                              ns: 'validation'
                            })
                        })
                    }
                  })}
                  error={errors?.totalInstalment?.message || !!errors?.totalInstalment?.type}
                  label={t(($) => $.editInstalment.form.total)}
                  type="number"
                  step="0.01"
                  className={styles['total-input']}
                  onChange={(e) => {
                    setValue('totalInstalment', roundToTwoDecimals(Number(e.target.value)))
                    handleInstalmentChange(Number(e.target.value))
                  }}
                  onWheel={(e) => e.currentTarget.blur()}
                  {...prefixOrSuffix}
                />
              </form>

              <div className={styles['slider-wrapper']}>
                <div className={styles['slider-container']}>
                  <Slider
                    className={styles['instalment-slider']}
                    min={minInstalment}
                    max={maxSliderBoundary}
                    step={5}
                    marks={sliderMarks}
                    onChange={(_, value) => {
                      const numericSliderInput = Number(value)
                      const totalRange = maxSliderBoundary - minInstalment

                      // Calculate dynamic percentage: scales from 2.5% to 5% based on total range
                      // Assumes a range of 0-500 for scaling (adjust these bounds as needed)
                      const minPercentage = 0.025
                      const maxPercentage = 0.05
                      const scalingFactor = Math.min(totalRange / 450, 1) // Caps at 1 when range >= 450
                      const dynamicPercentage = minPercentage + (maxPercentage - minPercentage) * scalingFactor
                      const percentualRange = totalRange * dynamicPercentage

                      // Find if value is within the percentual range near any mark
                      const nearMark = sliderMarks.find((mark) => {
                        const distance = Math.abs(numericSliderInput - mark.value)
                        return distance <= percentualRange
                      })

                      const valueToUse = nearMark ? nearMark.value : numericSliderInput
                      handleInstalmentChange(valueToUse)
                    }}
                    value={watchTotalInstalment}
                    slots={{
                      mark: (props) => (
                        <CustomSliderMarker
                          valueToSet={sliderMarks.some((mark) => mark.id === 'proposed') ? proposedInstalment : currentInstalment}
                          {...{ setHoveringOnMark, hoveringOnMark, handleInstalmentChange }}
                          {...props}
                        />
                      ),
                      markLabel: (props) => (
                        <CustomSliderMarkerLabel
                          valueToSet={sliderMarks.some((mark) => mark.id === 'proposed') ? proposedInstalment : currentInstalment}
                          {...{ setHoveringOnMark, hoveringOnMark, handleInstalmentChange }}
                          {...props}
                        />
                      )
                    }}
                  />
                  <small className={classNames(styles['mark-label'], styles['mark-label-min'])}>
                    {formatCurrency(minInstalment, { language })}
                  </small>
                  <small className={classNames(styles['mark-label'], styles['mark-label-max'])}>
                    {formatCurrency(maxSliderBoundary, { language })}
                  </small>
                </div>
              </div>

              {billShockAmount !== null && (
                <Card color="neutral" className={styles['estimated-settlement-card']}>
                  <dl>
                    <EstimatedSettlement settlementAmount={billShockAmount} loading={refetchingBillShock} alwaysOneLine />
                  </dl>
                </Card>
              )}

              {hasEffectiveGasContract && (
                <>
                  <Heading as="h2" variant="h5">
                    {t(($) => $.editInstalment.breakdown.title)}
                  </Heading>

                  <InstalmentCard
                    totalLabel={t(($) => $.editInstalment.breakdown.total)}
                    totalInstalment={watchTotalInstalment}
                    electricityInstalment={watchElectricityInstalment}
                    gasInstalment={watchGasInstalment}
                  />

                  <p className="mb-400">
                    <Trans
                      {...{ t }}
                      i18nKey={($) => $.editInstalment.breakdown.edit}
                      components={[
                        <Button
                          key="toggle-breakdown"
                          representation="link"
                          variant="inline"
                          className="weight-semibold"
                          onClick={() => setShowBreakdownModal(true)}
                        />
                      ]}
                    />
                  </p>
                </>
              )}

              <Button
                type="submit"
                form="total-instalment"
                isFullwidth
                disabled={IS_PROD_TESTING || !isValid || !checkHasInstalmentChanges() || instalmentSaved}
                loading={loading || submitting || refetchingBillShock}
              >
                {isMobile
                  ? t(($) => $.editInstalment.form.buttonMobile, {
                      newInstalment: formatCurrency(watchTotalInstalment, { language })
                    })
                  : t(($) => $.editInstalment.form.button, {
                      newInstalment: formatCurrency(watchTotalInstalment, { language })
                    })}
              </Button>

              {showBreakdownModal && (
                <InstalmentBreakdownDrawer
                  isOpen={showBreakdownModal}
                  onRequestClose={() => setShowBreakdownModal(false)}
                  onNewInstalmentSubmit={(newInstalment) => {
                    handleInstalmentChange(newInstalment.total, newInstalment.electricity, newInstalment.gas, true)
                  }}
                  instalment={{
                    total: watchTotalInstalment,
                    electricity: watchElectricityInstalment,
                    gas: watchGasInstalment
                  }}
                  ratios={{
                    electricity: electricityRatio,
                    gas: gasRatio
                  }}
                  range={{
                    min: minInstalment,
                    max: MAXIMUM_INSTALMENT_INPUT
                  }}
                />
              )}
            </>
          )}
        </>
      )}
    </EditLayout>
  )
}

export default EditInstalment
